import React, { useState, useEffect } from 'react';
import { Download, FileText } from 'lucide-react';
import { reportAPI } from '../utils/api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import '../styles/Reports.css';

const toNum = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

function Reports() {
  const [reportType, setReportType] = useState('gst');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleGenerateReport = async (e) => {
    e.preventDefault();

    if (!fromDate || (reportType !== 'daily' && !toDate)) {
      alert(reportType === 'daily' ? 'Please select date' : 'Please select date range');
      return;
    }

    try {
      setLoading(true);
      let response;

      if (reportType === 'gst') {
        response = await reportAPI.getGSTReport(fromDate, toDate);
      } else if (reportType === 'non-gst') {
        response = await reportAPI.getNonGSTReport(fromDate, toDate);
      } else if (reportType === 'daily') {
        response = await reportAPI.getDailySalesReport(fromDate);
      } else if (reportType === 'sales-register') {
        response = await reportAPI.getSalesRegister(fromDate, toDate);
      } else if (reportType === 'purchase-report') {
        response = await reportAPI.getPurchaseReport(fromDate, toDate);
      }

      setReportData(response.data);
    } catch (error) {
      alert('Error generating report');
    } finally {
      setLoading(false);
    }
  };

  const getRowsForExport = (data, type) => {
    if (!data || data.length === 0) return [];

    if (type === 'sales-register') {
      return data.map((r) => ({
        Date: new Date(r.invoiceDate).toLocaleDateString(),
        'Invoice No': r.invoiceNumber,
        Customer: r.customerName,
        GSTIN: r.gstinNumber || '',
        State: r.state || '',
        Quantity: r.totalQuantity,
        'Gross Amount': toNum(r.grossAmount).toFixed(2),
        'Taxable Value': toNum(r.taxableValue).toFixed(2),
        CGST: toNum(r.cgstAmount).toFixed(2),
        SGST: toNum(r.sgstAmount).toFixed(2),
        IGST: toNum(r.igstAmount).toFixed(2),
        'Total Tax': toNum(r.totalTax).toFixed(2),
        'Grand Total': toNum(r.grandTotal).toFixed(2),
      }));
    }

    if (type === 'gst') {
      return data.map((r) => ({
        'HSN Code': r.hsnCode,
        'Product Name': r.productName,
        'Total Quantity': r.totalQuantity,
        'Taxable Value': toNum(r.taxableValue).toFixed(2),
        CGST: toNum(r.cgstAmount).toFixed(2),
        SGST: toNum(r.sgstAmount).toFixed(2),
        IGST: toNum(r.igstAmount).toFixed(2),
        'Total GST': toNum(r.totalGst).toFixed(2),
        'Grand Total': toNum(r.grandTotal).toFixed(2),
      }));
    }

    if (type === 'non-gst') {
      return data.map((r) => ({
        'Product Name': r.productName,
        'Total Quantity': r.totalQuantity,
        'Total Sales Amount': toNum(r.totalSalesAmount).toFixed(2),
        Category: r.category,
      }));
    }

    if (type === 'daily') {
      return data.map((r) => ({
        'Invoice No': r.invoiceNumber,
        Date: new Date(r.invoiceDate).toLocaleDateString(),
        Customer: r.fullName,
        Quantity: r.quantity,
        'Grand Total': toNum(r.grandTotal).toFixed(2),
      }));
    }

    if (type === 'purchase-report') {
      return data.map((r) => ({
        Date: new Date(r.purchaseDate).toLocaleDateString(),
        'Purchase No': r.purchaseNumber,
        Supplier: r.supplierName,
        GSTIN: r.supplierGSTIN || '',
        Quantity: r.totalQuantity,
        Subtotal: toNum(r.subtotal).toFixed(2),
        Tax: toNum(r.totalTax).toFixed(2),
        'Grand Total': toNum(r.grandTotal).toFixed(2),
        'Payment Mode': r.paymentMode,
      }));
    }

    return [];
  };

  const downloadExcel = () => {
    const rows = getRowsForExport(reportData, reportType);
    if (!rows.length) {
      alert('No data to export');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `Report-${reportType}.xlsx`);
  };

  const downloadPdfReport = () => {
    const rows = getRowsForExport(reportData, reportType);
    if (!rows.length) {
      alert('No data to export');
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const lineHeight = 6;
    const headers = Object.keys(rows[0]);
    const colWidth = (pageWidth - margin * 2) / headers.length;

    let y = 12;
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text(`Report - ${reportType}`, margin, y);
    y += 8;

    doc.setFillColor(15, 52, 96);
    doc.rect(margin, y - 4, pageWidth - margin * 2, lineHeight, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    headers.forEach((h, i) => {
      doc.text(String(h).substring(0, 20), margin + i * colWidth + 1, y);
    });

    y += lineHeight;
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');

    rows.forEach((row, idx) => {
      if (y > pageHeight - 12) {
        doc.addPage();
        y = 12;
      }
      if (idx % 2 === 0) {
        doc.setFillColor(245, 248, 255);
        doc.rect(margin, y - 4, pageWidth - margin * 2, lineHeight, 'F');
      }
      headers.forEach((h, i) => {
        const value = row[h] == null ? '' : String(row[h]);
        doc.text(value.substring(0, 22), margin + i * colWidth + 1, y);
      });
      y += lineHeight;
    });

    doc.save(`Report-${reportType}.pdf`);
  };

  const handlePrintReport = () => {
    window.print();
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Reports & Analytics</h1>
      </div>

      <div className="card">
        <form onSubmit={handleGenerateReport} className="report-filter">
          <div className="form-row">
            <div className="form-group">
              <label>Report Type</label>
              <select value={reportType} onChange={(e) => setReportType(e.target.value)}>
                <option value="gst">Monthly GST Report (HSN-wise)</option>
                <option value="non-gst">Monthly Non-GST Report</option>
                <option value="daily">Daily Sales Report</option>
                <option value="sales-register">Sales Register (Invoice-wise)</option>
                <option value="purchase-report">Purchase Report</option>
              </select>
            </div>

            <div className="form-group">
              <label>From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                required
              />
            </div>

            {reportType !== 'daily' && (
              <div className="form-group">
                <label>To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <button type="submit" className="btn btn-primary">
                Generate Report
              </button>
            </div>
          </div>
        </form>
      </div>

      {reportData.length > 0 && (
        <div className="card report-section">
          <div className="report-header">
            <h2>
              {reportType === 'gst' && 'Monthly GST Report (HSN-wise)'}
              {reportType === 'non-gst' && 'Monthly Non-GST Report'}
              {reportType === 'daily' && 'Daily Sales Report'}
              {reportType === 'sales-register' && 'Sales Register (Invoice-wise)'}
              {reportType === 'purchase-report' && 'Purchase Report'}
            </h2>
            <div className="report-actions">
              <button className="btn btn-secondary" onClick={handlePrintReport}>
                <FileText size={16} />
                Print
              </button>
              <button className="btn btn-secondary" onClick={downloadPdfReport}>
                <FileText size={16} />
                Export PDF
              </button>
              <button className="btn btn-secondary" onClick={downloadExcel}>
                <Download size={16} />
                Export Excel
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    {reportType === 'gst' && (
                      <>
                        <th>HSN Code</th>
                        <th>Product Name</th>
                        <th>Total Quantity</th>
                        <th>Taxable Value</th>
                        <th>CGST</th>
                        <th>SGST</th>
                        <th>IGST</th>
                        <th>Total GST</th>
                        <th>Grand Total</th>
                      </>
                    )}
                    {reportType === 'non-gst' && (
                      <>
                        <th>Product Name</th>
                        <th>Total Quantity</th>
                        <th>Total Sales Amount</th>
                        <th>Category</th>
                      </>
                    )}
                    {reportType === 'daily' && (
                      <>
                        <th>Invoice No</th>
                        <th>Date</th>
                        <th>Customer</th>
                        <th>Quantity</th>
                        <th>Grand Total</th>
                      </>
                    )}
                    {reportType === 'sales-register' && (
                      <>
                        <th>Date</th>
                        <th>Invoice No</th>
                        <th>Customer</th>
                        <th>GSTIN</th>
                        <th>State</th>
                        <th>Quantity</th>
                        <th>Gross Amount</th>
                        <th>Taxable Value</th>
                        <th>CGST</th>
                        <th>SGST</th>
                        <th>IGST</th>
                        <th>Total Tax</th>
                        <th>Grand Total</th>
                      </>
                    )}
                    {reportType === 'purchase-report' && (
                      <>
                        <th>Date</th>
                        <th>Purchase No</th>
                        <th>Supplier</th>
                        <th>GSTIN</th>
                        <th>Quantity</th>
                        <th>Subtotal</th>
                        <th>Tax</th>
                        <th>Grand Total</th>
                        <th>Payment Mode</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, idx) => (
                    <tr key={idx}>
                      {reportType === 'gst' && (
                        <>
                          <td>{row.hsnCode}</td>
                          <td>{row.productName}</td>
                          <td>{row.totalQuantity}</td>
                          <td>₹{parseFloat(row.taxableValue).toFixed(2)}</td>
                          <td>₹{parseFloat(row.cgstAmount || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(row.sgstAmount || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(row.igstAmount || 0).toFixed(2)}</td>
                          <td className="total-cell">
                            ₹{parseFloat(row.totalGst || 0).toFixed(2)}
                          </td>
                          <td className="grand-total-cell">
                            ₹{parseFloat(row.grandTotal || 0).toFixed(2)}
                          </td>
                        </>
                      )}
                      {reportType === 'non-gst' && (
                        <>
                          <td>{row.productName}</td>
                          <td>{row.totalQuantity}</td>
                          <td>₹{parseFloat(row.totalSalesAmount).toFixed(2)}</td>
                          <td>{row.category}</td>
                        </>
                      )}
                      {reportType === 'daily' && (
                        <>
                          <td>{row.invoiceNumber}</td>
                          <td>{new Date(row.invoiceDate).toLocaleDateString()}</td>
                          <td>{row.fullName}</td>
                          <td>{row.quantity}</td>
                          <td>₹{parseFloat(row.grandTotal).toFixed(2)}</td>
                        </>
                      )}
                      {reportType === 'sales-register' && (
                        <>
                          <td>{new Date(row.invoiceDate).toLocaleDateString()}</td>
                          <td>{row.invoiceNumber}</td>
                          <td>{row.customerName}</td>
                          <td>{row.gstinNumber}</td>
                          <td>{row.state}</td>
                          <td>{row.totalQuantity}</td>
                          <td>₹{parseFloat(row.grossAmount || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(row.taxableValue || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(row.cgstAmount || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(row.sgstAmount || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(row.igstAmount || 0).toFixed(2)}</td>
                          <td className="total-cell">₹{parseFloat(row.totalTax || 0).toFixed(2)}</td>
                          <td className="grand-total-cell">₹{parseFloat(row.grandTotal || 0).toFixed(2)}</td>
                        </>
                      )}
                      {reportType === 'purchase-report' && (
                        <>
                          <td>{new Date(row.purchaseDate).toLocaleDateString()}</td>
                          <td>{row.purchaseNumber}</td>
                          <td>{row.supplierName}</td>
                          <td>{row.supplierGSTIN || '-'}</td>
                          <td>{row.totalQuantity}</td>
                          <td>₹{parseFloat(row.subtotal || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(row.totalTax || 0).toFixed(2)}</td>
                          <td className="grand-total-cell">₹{parseFloat(row.grandTotal || 0).toFixed(2)}</td>
                          <td>{row.paymentMode}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Reports;
